/* eslint-disable no-unused-vars */
import { useSearchParams } from "next/navigation";
import React, {
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";

import { useClickOutside } from "./useClickOutside";

export type UseSearchProps<T> = {
  items: T[];
  getLabel: (item: T) => string;
  onSearchSubmit?: (value: string) => void;
};

export const useSearch = <T>(
  { items, getLabel, onSearchSubmit }: UseSearchProps<T>,
  forwardedRef?: React.Ref<{ clearInput: () => void }>
) => {
  const searchParams = useSearchParams();
  const initialValue = searchParams.get("search") || "";
  const [value, setValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const suggestionContainerRef = useRef<HTMLUListElement | null>(null);

  useClickOutside(inputRef, () => setSuggestions([]), suggestionContainerRef);

  const decodeHtmlEntity = (encoded: string) => {
    const doc = new DOMParser().parseFromString(encoded, "text/html");
    return doc.documentElement.textContent || "";
  };

  const filterItems = useCallback(
    (inputValue: string) => {
      return items
        .filter((item) =>
          getLabel(item)
            .toLocaleLowerCase()
            .startsWith(inputValue.toLowerCase())
        )
        .map((item) => decodeHtmlEntity(getLabel(item)));
    },
    [items, getLabel]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      setValue(inputValue);

      if (inputValue) {
        setSuggestions(filterItems(inputValue));
      } else {
        setSuggestions([]);
      }

      if (!inputValue) {
        setSuggestions([]);
        setSelectedIndex(null);
      }
    },
    [filterItems]
  );

  const handleOnFocus = useCallback(() => {
    if (value) {
      setSuggestions(filterItems(value));
    }
  }, [value, filterItems]);

  const handleClearInput = useCallback(() => {
    setValue("");
    setSuggestions([]);
    setSelectedIndex(null);
    inputRef.current?.focus();
  }, [inputRef]);

  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      handleClickSuggestion: (suggestion: string) => void,
      suggestionContainerRef: RefObject<HTMLUListElement>
    ) => {
      if (e.key === "ArrowDown") {
        if (suggestions.length === 0) return;
        if (
          selectedIndex === null ||
          selectedIndex === suggestions.length - 1
        ) {
          setSelectedIndex(0);
          setValue(suggestions[0] ?? "");
        } else {
          setSelectedIndex(selectedIndex + 1);
          setValue(suggestions[selectedIndex + 1] ?? "");
        }
      } else if (e.key === "ArrowUp") {
        if (selectedIndex === null || selectedIndex === 0) {
          setSelectedIndex(suggestions.length - 1);
          setValue(suggestions[suggestions.length - 1] ?? "");
        } else {
          setSelectedIndex(selectedIndex - 1);
          setValue(suggestions[selectedIndex - 1] ?? "");
        }
      } else if (e.key === "Enter" && selectedIndex !== null) {
        const selectedSuggestion = suggestions[selectedIndex];
        if (onSearchSubmit) {
          setValue(selectedSuggestion);
          setSuggestions([]);
          onSearchSubmit(selectedSuggestion);
        } else {
          handleClickSuggestion(selectedSuggestion);
        }
        inputRef.current?.blur();
      } else if (e.key === "Enter") {
        setSuggestions([]);
      }

      if (suggestionContainerRef.current && selectedIndex !== null) {
        const selectedItem = suggestionContainerRef.current.children[
          selectedIndex
        ] as HTMLElement;
        if (selectedItem) {
          const container = suggestionContainerRef.current;
          const containerHeight = container.clientHeight;
          const itemTop = selectedItem.offsetTop;
          const itemHeight = selectedItem.clientHeight;
          const itemBottom = itemTop + itemHeight;
          const margin = 100;

          if (itemBottom + margin > container.scrollTop + containerHeight) {
            container.scrollTo({
              top: itemBottom - containerHeight + margin,
              behavior: "smooth"
            });
          } else if (itemTop - margin < container.scrollTop) {
            container.scrollTo({
              top: itemTop - margin,
              behavior: "smooth"
            });
          }

          if (
            selectedIndex === null ||
            (selectedIndex === suggestions.length - 1 &&
              container.scrollTop !== 0 &&
              e.key === "ArrowDown")
          ) {
            container.scrollTo({
              top: 0,
              behavior: "smooth"
            });
          }

          if (
            selectedIndex === 0 &&
            container.scrollTop === 0 &&
            e.key === "ArrowUp"
          ) {
            const lastItem = container.children[
              suggestions.length - 1
            ] as HTMLElement;
            if (lastItem) {
              const lastItemTop = lastItem.offsetTop;
              container.scrollTo({
                top: lastItemTop,
                behavior: "smooth"
              });
            }
          }
        }
      }
    },
    [onSearchSubmit, selectedIndex, suggestions]
  );

  const handleClickSuggestion = useCallback(
    (suggestion: string, setFilters: (filters: T[]) => void) => {
      const filteredItems = items.filter(
        (item) => getLabel(item) === suggestion
      );
      setFilters(filteredItems);
      setValue(suggestion);
      setSuggestions([]);
      inputRef.current?.blur();
    },
    [items, getLabel, inputRef]
  );

  useImperativeHandle(
    forwardedRef,
    () => ({
      clearInput: handleClearInput
    }),
    [handleClearInput]
  );

  useEffect(() => {
    const newValue = searchParams.get("search") || "";
    setValue(newValue);
  }, [searchParams]);

  return {
    value,
    suggestions,
    selectedIndex,
    inputRef,
    suggestionContainerRef,
    setValue,
    handleOnFocus,
    handleKeyDown,
    setSuggestions,
    handleClearInput,
    handleInputChange,
    handleClickSuggestion
  };
};
